import { useCallback, useEffect, useRef, useState } from 'react'

import type {
    StationKind,
    StationReadinessPayload,
} from '@/lib/api/station-readiness-api'

import { stationReadinessApi } from '@/lib/api/station-readiness-api'

const HEARTBEAT_INTERVAL_MS = 30_000
const REQUEST_TIMEOUT_MS = 15_000
let browserSessionID: string | undefined

// A rejection the next beat cannot fix: the session is gone (401) or the
// account lacks `station.view` (403). Retrying every 30s from an always-on
// station screen turns one misconfigured account into thousands of failed
// requests a day, which drowns real errors in telemetry. Reporting resumes on a
// remount, or on the single probe fired when the screen is returned to.
export type StationReadinessBlock = 'unauthorized' | 'forbidden'

function unrecoverableReason(err: unknown): StationReadinessBlock | undefined {
    const status = (err as { status?: number } | null)?.status
    if (status === 401) return 'unauthorized'
    if (status === 403) return 'forbidden'
    return undefined
}

interface StationReadinessState {
    sseConnected: boolean
    soundEnabled: boolean
    audioBlocked: boolean
    /** Bỏ trống = máy trạm đơn hàng. */
    kind?: StationKind
}

function stationSessionID(): string {
    browserSessionID ??= crypto.randomUUID()
    return browserSessionID
}

export function useStationReadinessHeartbeat(
    state: StationReadinessState,
    enabled = true,
) {
    const [blocked, setBlocked] = useState<StationReadinessBlock>()
    const [blockedAt, setBlockedAt] = useState<number>()
    const [requestId, setRequestId] = useState<string>()
    const sessionIDRef = useRef<string | null>(null)
    const stateRef = useRef(state)
    const inFlightRef = useRef(false)
    const pendingRef = useRef(false)
    const mountedRef = useRef(true)
    const controllerRef = useRef<AbortController | null>(null)
    const stoppedRef = useRef(false)
    const warnedRef = useRef(false)
    stateRef.current = state

    const report = useCallback(() => {
        if (!enabled) return
        if (stoppedRef.current) return
        if (inFlightRef.current) {
            pendingRef.current = true
            return
        }
        if (sessionIDRef.current === null) {
            sessionIDRef.current = stationSessionID()
        }
        const current = stateRef.current
        const payload: StationReadinessPayload = {
            session_id: sessionIDRef.current,
            sse_connected: current.sseConnected,
            sound_enabled: current.soundEnabled,
            audio_blocked: current.audioBlocked,
            ...(current.kind ? { kind: current.kind } : {}),
        }
        inFlightRef.current = true
        const controller = new AbortController()
        controllerRef.current = controller
        let released = false
        const timeout = window.setTimeout(() => {
            controller.abort()
            release()
        }, REQUEST_TIMEOUT_MS)
        const release = () => {
            if (released) return
            released = true
            window.clearTimeout(timeout)
            if (controllerRef.current === controller) {
                controllerRef.current = null
            }
            inFlightRef.current = false
            if (mountedRef.current && pendingRef.current) {
                pendingRef.current = false
                report()
            }
        }
        void stationReadinessApi
            .report(payload, controller.signal)
            .then(() => {
                if (!mountedRef.current) return
                setBlocked(undefined)
                setBlockedAt(undefined)
                setRequestId(undefined)
            })
            .catch((err: unknown) => {
                const reason = unrecoverableReason(err)
                if (!reason) {
                    // Readiness is retried by the next scheduled or browser
                    // event.
                    return
                }
                stoppedRef.current = true
                pendingRef.current = false
                if (mountedRef.current) {
                    setBlocked(reason)
                    setBlockedAt((previous) => previous ?? Date.now())
                    setRequestId(
                        (err as { requestId?: string } | null)?.requestId,
                    )
                }
                // Tracked separately from the circuit itself: the circuit
                // reopens on every return to the screen, and staff switch tabs
                // all day. One warning per mount says everything a repeated one
                // would, without burying the console it is meant to inform.
                if (warnedRef.current) return
                warnedRef.current = true
                console.warn(
                    'station readiness heartbeat stopped: the server rejected it as unauthorized. Grant the station account the `station.view` permission, then return to this screen to retry.',
                )
            })
            .finally(release)
    }, [enabled])

    // Returning to the screen is the one moment worth spending a request on a
    // circuit that has been declared dead: the usual reason it broke is a
    // missing grant, and the usual fix is someone adding it while the screen sat
    // untouched. Guarding on the broken state keeps this to a single probe —
    // returning to a tab raises visibilitychange and focus together, and a probe
    // that is rejected again re-breaks the circuit rather than reopening the
    // 30-second retry loop.
    const revive = useCallback(() => {
        if (!stoppedRef.current) return
        stoppedRef.current = false
        report()
    }, [report])

    useEffect(() => {
        if (!enabled) {
            stoppedRef.current = false
            pendingRef.current = false
            setBlocked(undefined)
            setBlockedAt(undefined)
            setRequestId(undefined)
            return
        }
        report()
    }, [
        enabled,
        state.sseConnected,
        state.soundEnabled,
        state.audioBlocked,
        report,
    ])

    useEffect(() => {
        if (!enabled) return
        mountedRef.current = true
        const interval = window.setInterval(report, HEARTBEAT_INTERVAL_MS)
        const onOnline = () => report()
        const onFocus = () => revive()
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return
            if (stoppedRef.current) {
                revive()
                return
            }
            report()
        }
        window.addEventListener('online', onOnline)
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => {
            mountedRef.current = false
            pendingRef.current = false
            controllerRef.current?.abort()
            controllerRef.current = null
            window.clearInterval(interval)
            window.removeEventListener('online', onOnline)
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [enabled, report, revive])

    return { blocked, blockedAt, requestId }
}
